import { AnimatePresence, motion, type Transition } from 'framer-motion';

import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';

import { ISLAND_GLASS_DARK, ISLAND_GLASS_LAYOUT_ID } from '../styles';
import type { IslandMode } from '../types';

type PillMode = Extract<IslandMode, { kind: 'link' | 'cta' | 'purchase' }>;

/** The round accent button at the pill's trailing edge — Apple's circular icon.
 *  The arrow nudges right on pill hover (drives off the `group` on the
 *  clickable); the circle itself stays put. */
const ArrowCircle = () => (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white">
        <ArrowRight className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
    </span>
);

/**
 * The island as a single dark-glass pill (link or CTA) — Apple's "Learn more…"
 * control: 56px / 3.5rem tall, `text-base`, label + a circular arrow button.
 *
 * The glass pill carries `layout`, so when the label changes (e.g. swapping from
 * "Read more about X" to "See more products by X") the pill **morphs its width**
 * while the label cross-fades — no disappear / reappear. Keeping the same React
 * element across link → link swaps is what lets framer-motion animate the box.
 */
export const PillIslandContent = ({
    mode,
    reduce,
    transition,
}: {
    mode: PillMode;
    reduce: boolean;
    transition: Transition;
}) => {
    // Purchase pill: grey price beside the CTA label — one cross-fading unit,
    // so the key covers both (a variant pick changes the price mid-flight).
    const price = mode.kind === 'purchase' ? mode.price : null;

    const pill = (
        <motion.div
            layout="size"
            layoutId={reduce ? undefined : ISLAND_GLASS_LAYOUT_ID}
            transition={transition}
            className={cn(
                ISLAND_GLASS_DARK,
                'flex h-14 items-center gap-3 rounded-full py-2 pr-2 pl-6',
            )}
        >
            <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                    key={`${price ?? ''}-${mode.label}`}
                    layout="position"
                    initial={reduce ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
                    transition={transition}
                    className="flex items-baseline gap-2.5 font-sans text-base font-medium whitespace-nowrap"
                >
                    {price ? (
                        <span className="font-normal text-white/60">
                            {price}
                        </span>
                    ) : null}
                    {mode.label}
                </motion.span>
            </AnimatePresence>
            <ArrowCircle />
        </motion.div>
    );

    return mode.kind === 'link' ? (
        <RoccoLink
            to={mode.to}
            onClick={mode.onClick}
            className="group pointer-events-auto block"
        >
            {pill}
        </RoccoLink>
    ) : (
        <button
            type="button"
            onClick={mode.onClick}
            disabled={mode.disabled}
            className="group pointer-events-auto block disabled:opacity-50"
        >
            {pill}
        </button>
    );
};
